// MIDAS V3 — Source card (MIDAS-V3-BUGS-0519 sub-task 5)
//
// Ports the v2 `shared/chatBot/components/ChatSourceCard.tsx` visual to v3
// without MUI / @emotion. v2 reference (read-only):
//   - File: shared/chatBot/components/ChatSourceCard.tsx
//   - Used by: customer/midas/components/v2/ChatBotMessageV2.tsx (lines 860-931)
//     in a horizontal source-cards strip rendered below the bot's prose
//     when the chat is NOT on /midas-v2 (the artifact panel takes over there).
//
// Visual contract (proportional, NOT pixel-identical — v3 uses Sora/mono not
// MUI's default cascade, palette is var(--token)-driven):
//   • Card: ~170px wide, rounded 8px, var(--card) bg, 1px var(--border).
//   • Image well: full-width × 90px tall. Token-aware placeholder shimmer
//     while loading; broken-image glyph on fetch error; no well at all when
//     the source has no `imageUrl`.
//   • Text strip: doc icon (📄) in accent colour + 2-line stack — "label"
//     (Sora 600 12px, ellipsis) over "name" (mono 11px var(--ink-2)).
//   • Click → caller's onClick({page}) — same dispatch path inline cite
//     chips already use (`handleCiteClick` in ChatBotMessageV3).
//
// Image loading mirrors v2 verbatim — auth Bearer fetch, blob URL, revoke
// on unmount. The proxy resolver is a copy of v2's so we don't import from
// the v2 file (CLAUDE.md: read v2, don't modify).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useEffect, useRef, useState } from "react";

import accountContext from "../../../../account/accountContext";
import { isResolvedApiImageUrl } from "../../../../shared/chatBot/botImageUrl";

// ═══════════════════════════════════════════════════════════════
//  Image proxy URL builder — copy of v2's helper (read-only ref).
// ═══════════════════════════════════════════════════════════════

/**
 * Resolve a bot image_url to our proxy endpoint. Accepts:
 *   1. An absolute URL (http(s)://...) — returned verbatim. Used by callers
 *      that already resolved the proxy path themselves, e.g. ChatBotMessageV3
 *      when feeding `message.artifacts[]` (diagram_overlay uses a different
 *      proxy path `/v1/bot/diagram/{page}/image` than image/platform_file's
 *      `/v1/bot/image/{path}`, so the caller builds the absolute URL up-front
 *      and passes it through unchanged — V2 parity, see V2 line 826-834 +
 *      MidasArtifactThumbnail.tsx:99-145).
 *   2. A relative `/api/v1/image/...` path or any URL containing that
 *      segment — returns `{baseUrl}/v1/bot/image/{stripped-path}` for our
 *      auth proxy. Used by `message.sources[].imageUrl` callers (existing
 *      cross-reference / source-card flows — currently dormant on MIDAS).
 */
const resolveImageProxyUrl = (imageUrl: string): string => {
  // Already-resolved endpoint: pass through unchanged. Callers that already built
  // the full proxy URL (e.g. diagram_overlay → /v1/bot/diagram/{page}/image) would
  // otherwise be mis-rewritten through the /v1/bot/image/ prefix.
  // Was an absolute-only `/^https?:\/\//i` test until 2026-08-19; that stopped
  // matching once apiBaseUrl became root-relative, double-proxying every diagram
  // source card. See shared/chatBot/botImageUrl.ts.
  if (isResolvedApiImageUrl(imageUrl)) return imageUrl;

  const baseUrl = (window as any).app?.config?.apiBaseUrl || "";
  const marker = "/api/v1/image/";
  const markerIdx = imageUrl.indexOf(marker);
  const stripped =
    markerIdx !== -1
      ? imageUrl.slice(markerIdx + marker.length)
      : imageUrl.replace(/^\/+/, "");
  return `${baseUrl}/v1/bot/image/${stripped}`;
};

// ═══════════════════════════════════════════════════════════════
//  Props
// ═══════════════════════════════════════════════════════════════

export interface ResponseSourceCardV3Props {
  /** Mono pill text, e.g. "Sheet 020" — drives the badge under the title. */
  label: string;
  /** Canonical join key. Forwarded back to onClick verbatim. */
  page: number;
  /** Localised page name, e.g. "DC 24V/10A Power Supply". Optional. */
  name?: string;
  /** Relative path to the bot image proxy (e.g. /api/v1/image/...).
   *  When undefined, the card renders without an image well. */
  imageUrl?: string;
  /** Click handler — caller dispatches openArtifactAtPage / openArtifact. */
  onClick?: (page: number) => void;
}

// ═══════════════════════════════════════════════════════════════
//  Component
// ═══════════════════════════════════════════════════════════════

const ResponseSourceCardV3: React.FC<ResponseSourceCardV3Props> = ({
  label,
  page,
  name,
  imageUrl,
  onClick,
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  const hasImage = !!imageUrl;
  const isClickable = !!onClick;

  // Fetch image via authenticated request and create a blob URL for <img src>.
  // Mirrors v2's ChatSourceCard fetch contract verbatim.
  useEffect(() => {
    if (!imageUrl) return;

    let revoked = false;
    const proxyUrl = resolveImageProxyUrl(imageUrl);
    const token = accountContext.accessToken;

    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    fetch(proxyUrl, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (revoked) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setThumbnailUrl(url);
      })
      .catch(() => {
        if (!revoked) setImageError(true);
      });

    return () => {
      revoked = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [imageUrl]);

  const handleClick = () => {
    if (onClick) onClick(page);
  };

  // Card chrome — plain <button> when clickable so the whole card is one tab
  // stop; <div> fallback for non-interactive (e.g. server gave no onClick).
  const Wrapper: React.ElementType = isClickable ? "button" : "div";

  return (
    <Wrapper
      type={isClickable ? "button" : undefined}
      onClick={isClickable ? handleClick : undefined}
      aria-label={
        isClickable
          ? `${label}${name ? ` — ${name}` : ""}`
          : undefined
      }
      style={{
        width: hasImage ? 170 : 200,
        flexShrink: 0,
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 8,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        padding: 0,
        fontFamily: "inherit",
        color: "inherit",
        cursor: isClickable ? "pointer" : "default",
        transition:
          "border-color .15s ease, box-shadow .15s ease, transform .15s ease",
      }}
      onMouseEnter={
        isClickable
          ? (e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.borderColor = "var(--accent)";
              e.currentTarget.style.boxShadow =
                "0 2px 8px color-mix(in oklab, var(--accent) 18%, transparent)";
            }
          : undefined
      }
      onMouseLeave={
        isClickable
          ? (e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.borderColor = "var(--border)";
              e.currentTarget.style.boxShadow = "none";
            }
          : undefined
      }
    >
      {/* Image well */}
      {hasImage && (
        <div
          style={{
            width: "100%",
            height: 90,
            background: "var(--card-2)",
            borderBottom: "1px solid var(--border-soft)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {thumbnailUrl && !imageError ? (
            <img
              src={thumbnailUrl}
              alt={name || label}
              onError={() => setImageError(true)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : imageError ? (
            <span
              aria-hidden="true"
              style={{
                fontSize: 22,
                color: "var(--faint)",
                lineHeight: 1,
              }}
            >
              ▤
            </span>
          ) : (
            // Loading skeleton — palette-aware solid card-2 fill. We don't
            // run a keyframe-driven shimmer here because the bot proxy image
            // fetch typically settles in <300ms; a brief flat placeholder
            // reads as "loading" without animating something the user is
            // about to dismiss anyway. Reduced-motion users get the same.
            <div
              aria-hidden="true"
              style={{
                width: "100%",
                height: "100%",
                background: "var(--card-2)",
              }}
            />
          )}
        </div>
      )}

      {/* Text strip */}
      <div
        style={{
          padding: "8px 10px",
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          minWidth: 0,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            fontSize: 14,
            color: "var(--accent)",
            lineHeight: 1.2,
            flexShrink: 0,
            marginTop: 1,
          }}
        >
          📄
        </span>
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <div
            className="mono"
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: "var(--accent)",
              background: "var(--accent-soft)",
              padding: "1px 6px",
              borderRadius: 4,
              display: "inline-block",
              maxWidth: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginBottom: name ? 4 : 0,
            }}
          >
            {label}
          </div>
          {name && (
            <div
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: 12,
                fontWeight: 600,
                color: "var(--ink)",
                lineHeight: 1.3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={name}
            >
              {name}
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

export default ResponseSourceCardV3;
